import React from "react";

import { FullWidthHeroBlock as Value } from "@reactivated";

import {
    HeroLayoutProps,
    HeroLayoutSettings,
} from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";
import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { Themed } from "../../styles/themes";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock";

import styles from "./index.module.scss";

type Layout = Value["layout"];
type Height = Value["height"];

const isSide = (layout: Layout) => layout === "left" || layout === "right";

const heightToMinHeight = (height: Height): HeroLayoutSettings["minHeight"] => {
    switch (height) {
        case "large":
            return 480;
        case "small":
            return 325;
        case "viewport":
        case null:
            return "clamp(640px, 100dvh, 1080px)";
    }
    return assertNever(height);
};

const desktopSettings = (
    layout: Layout,
    base: HeroLayoutSettings,
): HeroLayoutSettings => {
    switch (layout) {
        case "top":
            return { ...base, contentAlignPrimary: "left/top" };
        case "left":
            return {
                ...base,
                contentAlignSecondary: "left/top",
                contentWidth: 5,
            };
        case "right":
            return {
                ...base,
                contentAlignSecondary: "right/bottom",
                contentWidth: 5,
            };
        case "centered":
        case null:
            return base;
    }
    return assertNever(layout);
};

const mobileSettings = (
    layout: Layout,
    base: HeroLayoutSettings,
): HeroLayoutSettings => {
    const mobileBase: HeroLayoutSettings = {
        ...base,
        contentWidth: 12,
    };
    switch (layout) {
        case "top":
        case "left":
        case "right":
            return { ...mobileBase, contentAlignPrimary: "left/top" };
        case "centered":
        case null:
            return mobileBase;
    }
    return assertNever(layout);
};

const getOverlayClass = (layout: Layout, hasContent: boolean) => {
    if (!hasContent) return undefined;
    switch (layout) {
        case "left":
        case "right":
            return undefined;
        case "top":
            return styles.overlayGradientTop;
        case "centered":
        case null:
            return styles.overlay;
    }
    return assertNever(layout);
};

export const FullWidthHeroBlock = ({ value }: { value: Value }) => {
    if (!value.media) return null;

    const desktopMedia = <MediaChooserBlock value={value.media} />;
    const mobileMedia =
        value.mobile_media && value.mobile_media.length > 0 ? (
            <MediaChooserBlock value={value.mobile_media} />
        ) : (
            desktopMedia
        );

    const hasContent = !!value.content && value.content.length > 0;

    const firstRichText = value.content?.find(
        (item) => item.type === "rich_text_stream",
    );

    const baseSettings: HeroLayoutSettings = {
        heroDirection: "row",
        heroPlacement: "backgroundFull",
        mediaPlacement: "background",
        mediaAspectRatio: "stretch",
        contentAlignPrimary: "middle",
        contentAlignSecondary: "middle",
        textAlignment: "left",
        contentWidth: 8,
        minHeight: heightToMinHeight(value.height),
    };

    const content = (breakpoint: "desktop" | "mobile") => {
        if (!hasContent) return null;
        const alignment =
            isSide(value.layout) && breakpoint === "desktop"
                ? ("none" as const)
                : ("center" as const);
        return (
            <>
                {value.content?.map((item) => {
                    switch (item.type) {
                        case "rich_text_stream":
                            return (
                                <RichTextStreamBlock
                                    key={item.id}
                                    value={{
                                        ...item.value,
                                        container_width: "none",
                                        alignment,
                                    }}
                                />
                            );
                        case "spacer":
                            return (
                                <SpacerBlock key={item.id} value={item.value} />
                            );
                        default:
                            return assertNever(item);
                    }
                })}
            </>
        );
    };

    const heroStyles: HeroLayoutProps["styles"] = {
        mediaClassName: getOverlayClass(value.layout, hasContent),
        contentClassName:
            firstRichText?.type === "rich_text_stream" &&
            firstRichText.value.size_adjustment === "1"
                ? styles.paddingExtra
                : styles.padding,
    };

    return (
        <Themed theme={value.theme}>
            <HeroSet
                layouts={{
                    desktop: {
                        content: content("desktop"),
                        media: desktopMedia,
                        settings: desktopSettings(value.layout, baseSettings),
                        styles: heroStyles,
                    },
                    mobile: {
                        content: content("mobile"),
                        media: mobileMedia,
                        settings: mobileSettings(value.layout, baseSettings),
                        styles: heroStyles,
                    },
                }}
            />
        </Themed>
    );
};
